import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

type Answer = { status: 'received'; message: string }

const field =
  'w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 text-[var(--color-foam)] outline-none placeholder:text-[var(--color-mist)] focus:border-[var(--color-cyan)]'
const button =
  'neon-ring w-full rounded-full bg-[var(--color-foam)] px-5 py-3 font-semibold text-[var(--color-void)] disabled:opacity-60'

function readable(err: unknown): string {
  const text = err instanceof Error ? err.message : ''
  if (text.includes('Too many') || text.includes('429')) return 'Too many requests from here. Try again later.'
  return 'Could not send your request. Try again shortly.'
}

/** Early-access request: AFOS 360 is invite only, and a platform admin sends the invite. */
export function RequestAccess({ initialEmail = '' }: { initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail)
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answer, setAnswer] = useState<Answer | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/afos360/access-requests', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), name: name.trim(), company: company.trim(), note: note.trim() }),
      })
      if (!res.ok) throw new Error(res.status === 429 ? 'Too many' : await res.text())
      setAnswer((await res.json()) as Answer)
    } catch (err) {
      setError(readable(err))
    } finally {
      setBusy(false)
    }
  }

  if (answer) {
    return (
      <div className="space-y-3 text-center" role="status">
        <p className="text-sm font-semibold text-[var(--color-foam)]">Request received</p>
        <p className="text-sm text-[var(--color-mist)]">{answer.message}</p>
        <Link to="/signin" className="block text-sm text-[var(--color-cyan)] hover:underline">
          Back to sign in
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Request early access">
      <input className={field} type="email" autoComplete="email" required maxLength={254} placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input className={field} autoComplete="name" required maxLength={120} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} />
      <input className={field} autoComplete="organization" maxLength={120} placeholder="Company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} />
      <textarea className={`${field} min-h-24`} maxLength={1000} placeholder="What would you like to build? (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button type="submit" disabled={busy || !email.trim() || !name.trim()} className={button}>
        {busy ? 'Sending…' : 'Request early access'}
      </button>
      {error && (
        <p role="alert" className="text-center text-sm text-[var(--color-rose)]">
          {error}
        </p>
      )}
    </form>
  )
}
